import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import type { CalendarPlayer } from './screen-utils';

const AVATAR_SIZE = 20;
const CHIP_RADIUS = 30;
const CHIP_GAP = 10;
const CHIP_PADDING_X = 8;
const CHIP_PADDING_Y = 6;

export type PlayerFilterChipProps = {
  player: CalendarPlayer;
  isActive: boolean;
  /** Receives the player's id. */
  onPress: (playerId: string) => void;
};

/**
 * Avatar chip used to filter the Calendar screen down to a single player.
 *
 * Both states are solid-fill pills floating on a soft shadow — white when resting, brand
 * navy when selected. `CalendarControls` pads the scroll content so the shadow spreads
 * instead of clipping at the scroll edge. The chip is never width-constrained, so the
 * player name always renders in full.
 */
function PlayerFilterChip({ player, isActive, onPress }: PlayerFilterChipProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const handlePress = useCallback(() => onPress(player.id), [onPress, player.id]);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={STRINGS.guardian.kids.showPlayer.replace('{name}', player.name)}
      onPress={handlePress}
      style={({ pressed }) => (pressed ? styles.pressed : null)}>
      <Stack
        direction="row"
        align="center"
        gap={scale(CHIP_GAP)}
        borderRadius={scale(CHIP_RADIUS)}
        paddingX={scale(CHIP_PADDING_X)}
        paddingY={scale(CHIP_PADDING_Y)}
        backgroundColor={isActive ? guardianColors['Main/Main'] : colors['0']}
        style={styles.shadow}>
        <Avatar size={scale(AVATAR_SIZE)} borderWidth={0} activeOpacity={1}>
          <Avatar.Image source={player.avatar} backgroundColor={guardianColors['2']} />
        </Avatar>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
          style={isActive ? styles.labelActive : styles.label}>
          {player.label}
        </Text>
      </Stack>
    </Pressable>
  );
}

const useStyles = createStyles(() => ({
  shadow: {
    shadowColor: '#0F225B',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  label: {
    color: guardianColors['10'],
  },
  labelActive: {
    color: colors['0'],
  },
  pressed: {
    opacity: 0.72,
  },
}));

export default memo(PlayerFilterChip);
